import React from "react";
import { Box, Typography, Skeleton, Alert, LinearProgress } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import {
  useRecentDevelopments,
  getRecentDevelopmentsStatus,
  NewsItem,
  DateRange,
} from "../hooks/useRecentDevelopments";

const formatDateRange = (dateRange?: DateRange): string | null => {
  if (!dateRange?.from || !dateRange?.to) return null;

  const from = dayjs(dateRange.from);
  const to = dayjs(dateRange.to);

  // If same year, only show year once at the end
  if (from.year() === to.year()) {
    return `${from.format("MMM D")} - ${to.format("MMM D, YYYY")}`;
  }

  return `${from.format("MMM D, YYYY")} - ${to.format("MMM D, YYYY")}`;
};

interface RecentDevelopmentsCardProps {
  ticker: string;
  companyName?: string;
}

const NewsItemRow: React.FC<{ item: NewsItem; isLast: boolean }> = ({ item, isLast }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mb: isLast ? 0 : 2.5,
        transition: "opacity 0.2s ease",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.9375rem",
          color: theme.palette.text.primary,
          lineHeight: 1.4,
          mb: 0.5,
        }}
      >
        {item.heading}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.8125rem",
          color: theme.palette.text.secondary,
          lineHeight: 1.6,
        }}
      >
        {item.description}
      </Typography>
    </Box>
  );
};

const RecentDevelopmentsCard: React.FC<RecentDevelopmentsCardProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const { data, isLoading, error } = useRecentDevelopments(ticker);
  const status = getRecentDevelopmentsStatus(data, isLoading, error);
  const items = data?.developments?.items ?? [];
  const dateRangeText = formatDateRange(data?.dateRange);

  if (status === "no-data") return null;

  if (status === "loading") {
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: theme.palette.text.disabled,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb: 2.5,
          }}
        >
          Recent Developments
        </Typography>

        {/* Skeleton news items */}
        <Box sx={{ mb: 2.5 }}>
          <Skeleton variant="text" width="75%" height={22} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="20%" height={16} sx={{ mb: 0.75 }} />
          <Skeleton variant="text" width="100%" height={16} />
          <Skeleton variant="text" width="90%" height={16} />
        </Box>
        <Box sx={{ mb: 2.5 }}>
          <Skeleton variant="text" width="65%" height={22} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="25%" height={16} sx={{ mb: 0.75 }} />
          <Skeleton variant="text" width="100%" height={16} />
          <Skeleton variant="text" width="85%" height={16} />
        </Box>
        <Box>
          <Skeleton variant="text" width="80%" height={22} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="18%" height={16} sx={{ mb: 0.75 }} />
          <Skeleton variant="text" width="95%" height={16} />
          <Skeleton variant="text" width="70%" height={16} />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <AutoAwesomeOutlinedIcon
            sx={{
              fontSize: 14,
              color: theme.palette.text.disabled,
            }}
          />
          <Typography
            sx={{
              fontSize: "0.6875rem",
              color: theme.palette.text.disabled,
              fontWeight: 500,
            }}
          >
            Summarized from recent news
          </Typography>
        </Box>
      </Box>
    );
  }

  if (status === "generating") {
    return (
      <Box sx={{ p: 2 }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: theme.palette.text.secondary,
            mb: 1,
          }}
        >
          Loading recent developments...
        </Typography>
        <LinearProgress
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.divider,
          }}
        />
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Alert severity="error" sx={{ m: 2, borderRadius: 0 }}>
        Unable to load recent developments
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2.5 }}>
      {/* Section Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: theme.palette.text.disabled,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          Recent Developments
        </Typography>
        {dateRangeText && (
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 500,
              color: theme.palette.text.disabled,
            }}
          >
            · {dateRangeText}
          </Typography>
        )}
      </Box>

      {/* News Items or Empty State */}
      {items.length === 0 && (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: theme.palette.text.secondary,
            fontStyle: "italic",
          }}
        >
          No recent developments to report
        </Typography>
      )}
      {items.length > 0 && (
        <Box>
          {items.map((item, index) => (
            <NewsItemRow key={index} item={item} isLast={index === items.length - 1} />
          ))}
        </Box>
      )}

      {/* AI Attribution Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <AutoAwesomeOutlinedIcon
          sx={{
            fontSize: 14,
            color: theme.palette.text.disabled,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.6875rem",
            color: theme.palette.text.disabled,
            fontWeight: 500,
          }}
        >
          Summarized from recent news
        </Typography>
      </Box>
    </Box>
  );
};

export default React.memo(RecentDevelopmentsCard);
